/* NW Wellness — Header
   Sticky scroll shadow, mobile drawer toggle, live cart count.
   Re-binds on Shopify section reload (theme editor). */

(function () {
  class NWHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-nw-drawer]');
      this.menuBtn = this.querySelector('[data-nw-action="menu"]');
      this.closeBtn = this.querySelector('[data-nw-action="close-menu"]');
      this.searchBtn = this.querySelector('[data-nw-action="search"]');

      if (this.menuBtn) this.menuBtn.addEventListener('click', () => this.openDrawer());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeDrawer());
      document.addEventListener('keydown', this.onKey);
      if (this.searchBtn) this.searchBtn.addEventListener('click', () => this.openSearch());

      // Live cart count refresh after add-to-cart events from Dawn or our own code
      document.addEventListener('cart:updated', this.refreshCart);
      document.addEventListener('cart:refresh', this.refreshCart);

      // Dual-element cart count: visual badges are aria-hidden; a permanent
      // polite live region announces changes to screen readers instead.
      this.querySelectorAll('[data-nw-cart-count]').forEach((el) => el.setAttribute('aria-hidden', 'true'));
      this.cartStatus = this.querySelector('[data-nw-cart-status]');
      if (!this.cartStatus) {
        this.cartStatus = document.createElement('span');
        this.cartStatus.className = 'visually-hidden';
        this.cartStatus.setAttribute('role', 'status');
        this.cartStatus.setAttribute('aria-live', 'polite');
        this.cartStatus.setAttribute('data-nw-cart-status', '');
        this.appendChild(this.cartStatus);
      }

      // Expose the sticky header's real height so sticky offsets elsewhere
      // (e.g. the collection filter rail) track the actual rendered height.
      this.setHeaderHeight();
      window.addEventListener('resize', this.setHeaderHeight);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('cart:updated', this.refreshCart);
      document.removeEventListener('cart:refresh', this.refreshCart);
      window.removeEventListener('resize', this.setHeaderHeight);
    }

    setHeaderHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    };

    onKey = (e) => {
      if (e.key === 'Escape') this.closeDrawer();
    };

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.hidden = false;
      // Allow the browser to apply [hidden]=false before flipping data-open for the transition
      requestAnimationFrame(() => {
        this.drawer.setAttribute('data-open', '');
        document.body.classList.add('nw-drawer-open');
        if (this.menuBtn) this.menuBtn.setAttribute('aria-expanded', 'true');
        const firstLink = this.drawer.querySelector('a, button');
        if (firstLink) firstLink.focus();
      });
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.hasAttribute('data-open')) return;
      this.drawer.removeAttribute('data-open');
      document.body.classList.remove('nw-drawer-open');
      if (this.menuBtn) {
        this.menuBtn.setAttribute('aria-expanded', 'false');
        this.menuBtn.focus();
      }
      setTimeout(() => { if (this.drawer) this.drawer.hidden = true; }, 300);
    }

    openSearch() {
      // If the search overlay (sections/nw-search.liquid) is on the page,
      // open it; otherwise fall through to Shopify's standard search route.
      if (document.querySelector('nw-search')) {
        document.dispatchEvent(new CustomEvent('search:open', { detail: { trigger: this.searchBtn } }));
      } else {
        window.location.href = (window.Shopify?.routes?.root || '/') + 'search';
      }
    }

    refreshCart = async () => {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        this.querySelectorAll('[data-nw-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          if (cart.item_count === 0) {
            el.setAttribute('hidden', '');
          } else {
            el.removeAttribute('hidden');
          }
        });
        if (this.cartStatus) {
          this.cartStatus.textContent = cart.item_count === 1 ? '1 item in basket' : `${cart.item_count} items in basket`;
        }
      } catch (_) { /* swallow — count just won't update */ }
    };
  }

  if (!customElements.get('nw-header')) {
    customElements.define('nw-header', NWHeader);
  }
})();
